/**
 * Överenskommelse om avstående från besittningsskydd – Bostad (Formulär R-10).
 *
 * Hyresnämndens blankett HN 2 / HN 4: the agreement and, at the same time,
 * the application to hyresnämnden. Page 1 is the form; page 2 is
 * hyresnämndens explanatory text, reproduced verbatim.
 *
 * Page 2 carries its own § 1–3: the first information point restarts the
 * numbering (`restartNumbering`), so they are real numbered sections in a
 * scope of their own.
 *
 * Legal text verbatim from `base-templates/Mall - Avstående från
 * besittningsskydd BOSTAD.docx` (Haymanot-approved). DO NOT paraphrase.
 */
import { em, evaluateCondition, group, h, list, nodes, option, optionList, p, pageBreak, section, strong, t, val } from '@hyranai/regta-document-ir';
import { dateVal, fieldRows, partySlot, propertySlot, shown, slotText, textVal, ticked } from '../common/index.js';
import { answer, flag, legalMeta, legalSigningBlock } from '../meddelande_18e/kit.js';
const LEGAL = legalMeta('base-templates/Mall - Avstående från besittningsskydd BOSTAD.docx');
const COTENANT = ' (medhyresgäst)';
/** Name / Gatuadress / "Postnummer / Ort" + extra rows — the HN box form. */
export function hnPartyRows(input, prefix, extra) {
    return nodes(fieldRows([
        ['Namn', [textVal(input, `${prefix}_name`)]],
        ['Gatuadress', [textVal(input, `${prefix}_address`)]],
        ['Postnummer / Ort', [textVal(input, `${prefix}_postal_code`, 'short'), t(' '), textVal(input, `${prefix}_city`)]],
        ...extra,
    ]));
}
/** "________ Datum" — a fill-in line followed by its caption. */
export const ruledCaption = (caption) => p([val(null, { blankWidth: 'long' }), t(` ${caption}`)]);
/**
 * A page-2 information point. The first one (`restart`) opens a numbering
 * scope of its own, so page 2 prints § 1, § 2, … after page 1's points.
 */
export const infoSection = (ref, title, content, restart = false) => section(ref, title, content, restart ? { restartNumbering: true } : {});
export const rubrik = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.rubrik',
    version: 'v1',
    title: 'Rubrik',
    summary: 'The read-the-information warning and the form’s title, “Ansökan till hyresnämnd”.',
    slots: [],
    compile: () => [
        p([strong('OBS! Läs informationen om avstående från besittningsskydd nedan innan du fyller i blanketten.')], {
            variant: 'caption',
        }),
        h(1, 'Överenskommelse om avstående från besittningsskydd – Bostad', { align: 'center', keepWithNext: true }),
        p('Ansökan till hyresnämnd', { align: 'center', variant: 'caption' }),
    ],
};
export const hyresvard = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.hyresvard',
    version: 'v1',
    title: 'Hyresvärd / Uthyrare',
    summary: 'The landlord’s name, address and contact details.',
    slots: [
        partySlot('landlord_name', 'Namn', 'lessor', 0, 'name', { required: true }),
        partySlot('landlord_address', 'Gatuadress', 'lessor', 0, 'street', { type: 'textarea', required: true }),
        partySlot('landlord_postal_code', 'Postnummer', 'lessor', 0, 'postalCode', { required: true }),
        partySlot('landlord_city', 'Ort', 'lessor', 0, 'city', { required: true }),
        partySlot('landlord_email', 'E-post', 'lessor', 0, 'email'),
        partySlot('landlord_phone_mobile', 'Telefon mobil', 'lessor', 0, 'phone'),
    ],
    compile: (input) => [
        section('hyresvard-uthyrare', 'Hyresvärd / Uthyrare', [
            ...hnPartyRows(input, 'landlord', [
                ['E-post', [textVal(input, 'landlord_email')]],
                ['Telefon mobil', [textVal(input, 'landlord_phone_mobile')]],
            ]),
        ]),
    ],
};
/** A co-tenant is present when any of its printed fields is filled. */
const COTENANT_PRESENT = {
    any: ['cotenant_name', 'cotenant_address', 'cotenant_phone_mobile'].map((key) => ({ key, isSet: true })),
};
const showCotenant = (input, ctx) => shown(ctx, evaluateCondition(COTENANT_PRESENT, input));
export const hyresgaster = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.hyresgaster',
    version: 'v1',
    title: 'Hyresgäst (-er)',
    summary: 'The tenant, and a co-tenant when one is given.',
    slots: [
        partySlot('tenant_name', 'Namn', 'lessee', 0, 'name', { required: true }),
        partySlot('tenant_address', 'Gatuadress', 'lessee', 0, 'street', { type: 'textarea', required: true }),
        partySlot('tenant_postal_code', 'Postnummer', 'lessee', 0, 'postalCode', { required: true }),
        partySlot('tenant_city', 'Ort', 'lessee', 0, 'city', { required: true }),
        partySlot('tenant_phone_mobile', 'Telefon mobil', 'lessee', 0, 'phone'),
        partySlot('cotenant_name', `Namn${COTENANT}`, 'lessee', 1, 'name'),
        partySlot('cotenant_address', `Gatuadress${COTENANT}`, 'lessee', 1, 'street', { type: 'textarea' }),
        partySlot('cotenant_postal_code', `Postnummer${COTENANT}`, 'lessee', 1, 'postalCode'),
        partySlot('cotenant_city', `Ort${COTENANT}`, 'lessee', 1, 'city'),
        partySlot('cotenant_phone_mobile', `Telefon mobil${COTENANT}`, 'lessee', 1, 'phone'),
        partySlot('spouse_name', 'Namn', 'other', 0, 'name', {
            help: 'Fyll i om make eller sambo ska underteckna överenskommelsen.',
        }),
    ],
    compile: (input, ctx) => [
        section('hyresgast-er', 'Hyresgäst (-er)', nodes(...hnPartyRows(input, 'tenant', [['Telefon mobil', [textVal(input, 'tenant_phone_mobile')]]]), showCotenant(input, ctx) ? p([strong('Medhyresgäst')], { keepWithNext: true }) : null, ...(showCotenant(input, ctx)
            ? hnPartyRows(input, 'cotenant', [['Telefon mobil', [textVal(input, 'cotenant_phone_mobile')]]])
            : [])), { sideBySide: true }),
    ],
};
export const lagenhetensAdress = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.lagenhetens-adress',
    version: 'v1',
    title: 'Lägenhetens adress',
    summary: 'Street, town and fastighetsbeteckning of the let apartment.',
    slots: [
        propertySlot('apartment_street', 'Gata', 'address', { required: true }),
        propertySlot('apartment_city', 'Ort', 'municipality', { required: true }),
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
    ],
    compile: (input) => [
        section('lagenhetens-adress', 'Lägenhetens adress', [
            ...nodes(fieldRows([
                ['Gata', [textVal(input, 'apartment_street')]],
                ['Ort', [textVal(input, 'apartment_city')]],
                ['Fastighetsbeteckning', [textVal(input, 'property_designation')]],
            ])),
        ]),
    ],
};
export const hyresavtalet = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.hyresavtalet',
    version: 'v1',
    title: 'Hyresavtalet',
    summary: 'Which lease the waiver concerns: its number, signing date and access date.',
    slots: [
        {
            key: 'lease_contract_number',
            label: 'Avtalsnummer',
            type: 'text',
            help: 'Valfritt. Avtalsnummer/referens på det hyresavtal som överenskommelsen avser. Förifylls från hyresavtalet när överenskommelsen skapas därifrån.',
        },
        {
            key: 'lease_signed_date',
            label: 'Dag då hyresavtalet undertecknades',
            type: 'date',
            required: true,
            help: 'Datum då det bostadshyresavtal som överenskommelsen avser undertecknades.',
        },
        {
            key: 'lease_access_date',
            label: 'Tillträdesdag',
            type: 'date',
            required: true,
            help: 'Tillträdesdag enligt hyresavtalet. Överenskommelsen gäller i fyra år från denna dag (se informationen om avstående från besittningsskydd).',
        },
    ],
    compile: (input, ctx) => [
        section('hyresavtalet', 'Hyresavtalet', nodes(p([em('(kopia på avtalet bör ges in)')], { variant: 'fine_print' }), fieldRows([
            shown(ctx, !!slotText(input.lease_contract_number)) && ['Avtalsnummer', [textVal(input, 'lease_contract_number')]],
            ['Dag då hyresavtalet undertecknades', [dateVal(ctx, input, 'lease_signed_date')]],
            ['Tillträdesdag', [dateVal(ctx, input, 'lease_access_date')]],
        ]))),
    ],
};
export const innehall = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.overenskommelsens-innehall',
    version: 'v1',
    title: 'Överenskommelsens innehåll',
    summary: 'The tenant waives extension if the landlord terminates for demolition/major rebuild or another stated cause; valid four years.',
    slots: [
        {
            key: 'reason_rivning_ombyggnad',
            label: 'Avtalet sägs upp på grund av att huset ska rivas eller undergå större ombyggnad',
            type: 'boolean',
            help: 'Kryssa i om hyresvärden förbehåller sig rätten att säga upp avtalet på grund av rivning eller större ombyggnad av huset.',
        },
        {
            key: 'reason_other',
            label: 'Avtalet sägs upp på grund av en annan orsak (specificera nedan)',
            type: 'boolean',
            help: 'Hyresnämnden godkänner i regel överenskommelsen endast om orsaken är godtagbar. Om hyresvärden senare säger upp avtalet av en annan orsak än den som anges här behåller hyresgästen sitt besittningsskydd.',
        },
        {
            key: 'reason_other_description',
            label: 'Beskrivning av annan orsak',
            type: 'textarea',
            visibleWhen: { key: 'reason_other', equals: true },
            help: 'Ange tydligt den orsak som ska gälla. Vagt formulerade skäl godkänns inte av hyresnämnden.',
        },
        {
            key: 'validity_extended_motivation',
            label: 'Motivering vid längre giltighetstid än fyra år',
            type: 'textarea',
            help: 'Lämnas tomt om överenskommelsen gäller standardperioden (fyra år från tillträdesdagen). Fyll i särskild motivering om parterna vill att överenskommelsen ska gälla för längre tid.',
        },
    ],
    compile: (input, ctx) => [
        section('overenskommelsens-innehall', 'Överenskommelsens innehåll', nodes(p('Vi kommer härmed överens om att hyresgästen (-erna) inte ska ha rätt till förlängning av hyresavtalet'), optionList([
            option(ticked(ctx, flag(input, 'reason_rivning_ombyggnad')), 'om hyresvärden säger upp avtalet på grund av att hyreslägenheten ligger i ett hus som ska rivas eller ska undergå en större ombyggnad, eller', { code: 'reason_rivning_ombyggnad', sourceKey: 'reason_rivning_ombyggnad' }),
            option(ticked(ctx, flag(input, 'reason_other')), 'om hyresvärden säger upp avtalet på grund av en annan orsak som anges tydligt i det följande (utom när hyresnämndens godkännande inte behövs, se informationen om avstående från besittningsskydd)', { code: 'reason_other', sourceKey: 'reason_other' }),
        ], { select: 'multi' }), shown(ctx, flag(input, 'reason_other')) ? answer(input, 'reason_other_description') : null, p([
            em('Normalt behövs inget avståendeavtal vid uthyrning av ett en- eller tvåfamiljshus, en ägarlägenhet eller vid upplåtelse i andra hand av en bostadsrättslägenhet, se informationen om avstående från besittningsskydd.'),
        ]), p([
            strong('Giltighetstid:'),
            t(' Denna överenskommelse gäller i fyra år från tillträdesdagen (se närmare informationen om avstående från besittningsskydd).'),
        ]), shown(ctx, !!slotText(input.validity_extended_motivation)) ? p('Motivering vid längre giltighetstid:', { keepWithNext: true }) : null, shown(ctx, !!slotText(input.validity_extended_motivation)) ? answer(input, 'validity_extended_motivation') : null)),
    ],
};
export const ansokan = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.ansokan',
    version: 'v1',
    title: 'Ansökan',
    summary: 'The parties apply for hyresnämnden’s approval.',
    slots: [],
    compile: () => [section('ansokan', 'Ansökan', [p('Vi ansöker härmed om att hyresnämnden godkänner vår överenskommelse.')])],
};
export const underskrift = legalSigningBlock(LEGAL, {
    blockKey: 'avstaende_besittningsskydd_bostad.underskrift',
    title: 'Underskrifter',
    summary: 'Signing statement and panels: landlord, tenant, and co-tenant / spouse only when present.',
    defaults: {
        ref: 'underskrift',
        title: 'Underskrifter',
        statement: true,
        parties: [
            { label: 'Hyresvärd / Uthyrare', role: 'lessor', ordinal: 0 },
            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
            { label: 'Eventuell medhyresgäst', role: 'lessee', ordinal: 1, visible_when: COTENANT_PRESENT },
            { label: 'Hyresgästens make/sambo', role: 'other', ordinal: 0, when_set: 'spouse_name' },
        ],
    },
});
export const hyresnamndensBeslut = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.hyresnamndens-beslut',
    version: 'v1',
    title: 'Hyresnämndens beslut',
    summary: 'The box hyresnämnden fills in: approval, date and signature.',
    slots: [],
    compile: () => [
        section('hyresnamndens-beslut', 'Hyresnämndens beslut', [
            p([em('Fylls i av hyresnämnden.')], { variant: 'fine_print' }),
            group([
                optionList([option(false, 'Överenskommelsen godtas')]),
                p([em('Observera att hyresnämndens godkännande inte innebär att nämnden har godkänt villkoren i hyresavtalet.')]),
                ruledCaption('Datum'),
                ruledCaption('Hyresnämndens underskrift'),
            ], { variant: 'boxed' }),
        ]),
    ],
};
export const information = {
    ...LEGAL,
    blockKey: 'avstaende_besittningsskydd_bostad.information',
    version: 'v1',
    title: 'Information om avstående från besittningsskydd – Bostad',
    summary: 'Hyresnämndens page-2 explanation: what besittningsskydd is, when it can be waived, HN 2 vs HN 4, where to find more.',
    slots: [],
    compile: () => [
        pageBreak(),
        h(1, 'Information om avstående från besittningsskydd – Bostad', { align: 'center', keepWithNext: true }),
        p([em('(Nya regler fr.o.m. den 1 februari 2013)')], { align: 'center', variant: 'fine_print' }),
        infoSection('besittningsskydd-vad-innebar-det', 'Besittningsskydd, vad innebär det?', [
            p('En bostadshyresgäst som har besittningsskydd har i regel rätt att få sitt hyresavtal förlängt när hyresvärden säger upp avtalet och begär att hyresgästen ska flytta. Är hyresvärden och hyresgästen inte ense om att avtalet ska upphöra, avgör hyresnämnden tvisten. Hyresgästen har rätt att bo kvar i lägenheten under den tid som nämnden prövar ärendet. I besittningsskyddet ligger också att hyresgästen kan få uppskov med avflyttningen. Besittningsskyddet gäller dock inte alltid t.ex. om hyresavtalet avser en vanlig andrahandsupplåtelse av en hyreslägenhet och avtalet upphör innan det har varat längre än två år i följd, se 12 kap. 45 § jordabalken (hyreslagen).'),
        ], true),
        infoSection('kan-besittningsskyddet-avtalas-bort', 'Kan besittningsskyddet avtalas bort?', [
            p('Om hyresvärden och hyresgästen i en särskilt upprättad handling har kommit överens om att hyresgästen avstår från sitt besittningsskydd, ska hyresnämnden som huvudregel godkänna överenskommelsen för att den ska vara giltig, men regeln har undantag.'),
            p([strong('När behövs hyresnämndens godkännande av överenskommelsen (blankett HN 2)?')], { keepWithNext: true }),
            p('Huvudregeln är att hyresnämnden ska godkänna en överenskommelse om avstående från besittningsskydd för att den ska vara giltig. Hyresvärden och hyresgästen bör då använda sig av den blankett som hyresnämnderna har fastställt (HN 2). Blanketten med överenskommelsen är samtidigt en ansökan till hyresnämnden och på blanketten kan också hyresnämnden skriva sitt beslut.'),
            p('För att hyresnämnden ska godkänna överenskommelsen måste en godtagbar orsak till en kommande uppsägning anges. Om hyresvärden säger upp hyresavtalet av en annan orsak än den som anges i överenskommelsen, har hyresgästen kvar sitt besittningsskydd. Normalt godkänner nämnden endast överenskommelser för sådana hyresförhållanden som upphör inom fyra år från tillträdesdagen. Blanketten är därför förtryckt med denna tid. Om parterna vill att överenskommelsen ska gälla för längre tid måste de motivera detta särskilt.'),
            p([strong('När behövs inte hyresnämndens godkännande av överenskommelsen (blankett HN 4)?')], { keepWithNext: true }),
            p('Om en överenskommelse om avstående från besittningsskydd träffas under en tid då hyresgästen har besittningsskydd, krävs inget godkännande av hyresnämnden. Parterna behöver då inte heller använda något särskilt formulär, men överenskommelsen ska ändå alltid träffas i en särskilt upprättad handling.'),
            p('Om en överenskommelse om avstående från besittningsskydd träffas för en tid på högst fyra år från tillträdesdagen gäller den utan hyresnämndens godkännande om den avser'),
            list([
                'en bostadslägenhet i ett en- eller tvåfamiljshus som inte ingår i en affärsmässigt bedriven uthyrningsverksamhet eller en bostadslägenhet inom en ägarlägenhetsfastighet som inte ingår i en affärsmässigt bedriven uthyrningsverksamhet och den som hyr ut ska bosätta sig där eller ska överlåta huset eller ägarlägenheten',
                'en hyreslägenhet som upplåts i andra hand och upplåtaren ska bosätta sig i lägenheten',
                'en bostadsrättslägenhet som upplåts i andra hand och bostadsrättshavaren ska bosätta sig där eller ska överlåta bostadsrätten.',
            ]),
            p([
                strong('OBS!'),
                t(' Vid en upplåtelse enligt lagen om uthyrning av egen bostad (”privatuthyrningslagen”) saknar hyresgästen helt besittningsskydd. Så är till exempel fallet när en privatperson upplåter en lägenhet i ett en- eller tvåfamiljshus, inom en ägarlägenhetsfastighet eller upplåter en bostadsrättslägenhet i andra hand. Parterna behöver alltså inte avtala om avstående från besittningsskydd vid sådana upplåtelser. Om hyresvärden däremot upplåter två lägenheter gäller ”privatuthyrningslagen” bara för den första upplåtelsen. För den andra upplåtelsen gäller reglerna om besittningsskydd i 12 kap. jordabalken (hyreslagen). Den som hyr ut mer än två lägenheter anses normalt bedriva uthyrning i näringsverksamhet och då gäller hyreslagens regler om besittningsskydd för samtliga upplåtelser.'),
            ]),
            p([em('Hyresnämndens blankett HN 4 motsvarar regeringens formulär 1-3, förordningen SFS 2005:1148.')], { variant: 'fine_print' }),
        ]),
        infoSection('var-finns-blanketter-och-mer-information', 'Var finns blanketter och mer information?', [
            p('Du kan hämta blanketter hos hyresnämnden eller på hyresnämndernas webbplats www.hyresnamnden.se. Där finns också mer information om ”privatuthyrningslagen” (under ”Skälig hyra”) och om andra hyresrättsliga frågor. Hyresnämnderna har även telefonupplysning under de tider som framgår av varje nämnds kontaktuppgifter på webbplatsen.'),
        ]),
    ],
};
export const AVSTAENDE_BESITTNINGSSKYDD_BOSTAD_BLOCKS = [
    rubrik,
    hyresvard,
    hyresgaster,
    lagenhetensAdress,
    hyresavtalet,
    innehall,
    ansokan,
    underskrift,
    hyresnamndensBeslut,
    information,
];
//# sourceMappingURL=blocks.js.map